import { useCallback, useEffect, useState } from "react";
import { Building2, Globe, Save, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, usePolling } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Spinner, Tag, Alert, WorkingTerminal } from "@/components/common";
import { ListEditor, ServicesEditor, TestimonialsEditor } from "@/components/profile/Editors";
import { PasteReviews } from "@/components/profile/PasteReviews";

const LISTS = [
  ["offers", "Offers & packages"], ["usps", "Unique selling points"], ["ideal_customers", "Ideal customers"],
  ["calls_to_action", "Calls to action"], ["service_areas", "Service areas"], ["trust_signals", "Trust signals"],
];
const CONTACT = ["phone", "email", "whatsapp", "address", "opening_hours"];

const Field = ({ label, children }) => (<label className="block"><span className="cc-eyebrow">{label}</span><div className="mt-2">{children}</div></label>);

export default function BusinessProfile() {
  const [doc, setDoc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [url, setUrl] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/profile");
    setDoc(data.profile_doc);
    setUrl((u) => u || data.profile_doc?.website || data.default_website);
    if (data.profile_doc?.status !== "crawling") setDraft(data.profile_doc?.profile ? structuredClone(data.profile_doc.profile) : null);
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);
  const crawling = doc?.status === "crawling";
  usePolling(load, crawling, 3000);

  const crawl = async () => {
    try {
      const { data } = await api.post("/profile/crawl", { url });
      setDoc(data.profile_doc);
      toast.message("Crawling website and extracting profile…");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { profile: draft });
      setDoc(data.profile_doc);
      toast.success("Business profile saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const [importing, setImporting] = useState(false);
  const importTestimonials = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/profile/testimonials/import");
      set("testimonials", data.testimonials);
      toast.success(`Found ${data.found} verbatim testimonials on the website`);
    } catch (e) { toast.error(errMsg(e)); } finally { setImporting(false); }
  };
  const setContact = (k, v) => setDraft((d) => ({ ...d, contact: { ...(d.contact || {}), [k]: v } }));

  if (!loaded) return <Spinner />;

  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Website study" title="Business Profile"
        description="Ads Monster crawls your homepage and key service pages, then Claude extracts the facts every campaign is built on. Edit anything that is off."
        actions={draft && <button data-testid="profile-save-button" onClick={save} disabled={saving || crawling} className="cc-btn cc-btn-primary">{saving ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Save className="h-4 w-4" />} Save profile</button>} />

      <Panel title="Source website" icon={Globe} testid="crawl-panel" className="mb-6">
        <div className="flex flex-col gap-3 md:flex-row">
          <input data-testid="crawl-url-input" className="cc-input font-mono" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button data-testid="crawl-start-button" onClick={crawl} disabled={crawling || !url} className="cc-btn cc-btn-primary whitespace-nowrap">
            {crawling ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <ScanLine className="h-4 w-4" />} {doc?.profile ? "Re-crawl & rebuild" : "Crawl & build profile"}
          </button>
        </div>
        {doc?.crawled_at && (
          <div className="mt-4 flex flex-wrap items-center gap-2 font-mono text-xs text-slate-500" data-testid="crawl-meta">
            Crawled {fmtDate(doc.crawled_at)} · {doc.pages.length} pages {doc.edited && <Tag tone="cyan">Edited by owner</Tag>}
          </div>
        )}
        {doc?.pages?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="crawled-pages-list">
            {doc.pages.map((p) => <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="rounded border border-[#1e293b] px-2 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-emerald-500/40 hover:text-emerald-300">{p.url.replace(/^https?:\/\/(www\.)?/, "")}</a>)}
          </div>
        )}
        {doc?.status === "error" && <div className="mt-4"><Alert testid="crawl-error" title="Crawl failed">{doc.error}</Alert></div>}
      </Panel>

      {crawling && <WorkingTerminal testid="crawl-progress" title="Studying the website with Claude Sonnet 5.5" lines={["Fetching homepage and service pages", "Stripping navigation noise", "Extracting services, offers, USPs, tone and CTAs"]} />}

      {!crawling && !draft && (
        <EmptyState testid="profile-empty-state" icon={Building2} title="No business profile yet" description="Run the crawl to build a real profile from therehablabsg.com. Nothing is invented — fields the site doesn’t mention stay empty." />
      )}

      {!crawling && draft && (
        <div className="space-y-6" data-testid="profile-editor">
          <Panel title="Identity" icon={Building2}>
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Business name"><input data-testid="profile-business-name-input" className="cc-input" value={draft.business_name || ""} onChange={(e) => set("business_name", e.target.value)} /></Field>
              <Field label="Location"><input data-testid="profile-location-input" className="cc-input" value={draft.location || ""} onChange={(e) => set("location", e.target.value)} /></Field>
              <Field label="Monthly Google Ads budget (SGD)"><input data-testid="profile-monthly-budget-input" type="number" min="0" step="50" className="cc-input font-mono" value={draft.monthly_budget ?? ""} onChange={(e) => set("monthly_budget", e.target.value === "" ? null : Number(e.target.value))} /></Field>
              <Field label="New AI campaigns share (auto: whole monthly budget, the old campaign is being paused)"><input data-testid="profile-new-campaigns-budget-input" type="number" readOnly disabled className="cc-input font-mono opacity-70" value={draft.monthly_budget ?? ""} /></Field>
              <div className="md:col-span-2"><Field label="Summary"><textarea data-testid="profile-summary-input" rows={3} className="cc-input resize-y" value={draft.summary || ""} onChange={(e) => set("summary", e.target.value)} /></Field></div>
              <div className="md:col-span-2"><Field label="Tone of voice"><textarea data-testid="profile-tone-input" rows={2} className="cc-input resize-y" value={draft.tone || ""} onChange={(e) => set("tone", e.target.value)} /></Field></div>
            </div>
          </Panel>
          <Panel><ServicesEditor items={draft.services || []} onChange={(v) => set("services", v)} /></Panel>
          <Panel title="Social proof">
            <div className="mb-6 grid gap-5 md:grid-cols-2">
              <Field label="Google rating"><input data-testid="profile-rating-input" type="number" step="0.1" min="0" max="5" className="cc-input font-mono" value={draft.google_rating?.rating ?? ""} onChange={(e) => set("google_rating", { ...(draft.google_rating || {}), rating: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
              <Field label="Google review count"><input data-testid="profile-review-count-input" type="number" min="0" className="cc-input font-mono" value={draft.google_rating?.count ?? ""} onChange={(e) => set("google_rating", { ...(draft.google_rating || {}), count: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
              <Field label="Google reviews URL (optional, makes rating cards clickable)"><input data-testid="profile-google-reviews-url-input" type="url" placeholder="https://g.page/r/…/review" className="cc-input" value={draft.google_reviews_url || ""} onChange={(e) => set("google_reviews_url", e.target.value)} /></Field>
              <Field label="Slots available this week (optional, blank hides it)"><input data-testid="profile-slots-week-input" type="number" min="0" step="1" className="cc-input font-mono" value={draft.slots_this_week ?? ""} onChange={(e) => set("slots_this_week", e.target.value === "" ? null : Math.max(0, Math.floor(Number(e.target.value))))} /></Field>
            </div>
            <PasteReviews onSaved={(v) => set("testimonials", v)} />
            <TestimonialsEditor items={draft.testimonials || []} onChange={(v) => set("testimonials", v)} onImport={importTestimonials} importing={importing} />
          </Panel>
          <div className="grid gap-6 lg:grid-cols-2">
            {LISTS.map(([k, label]) => <Panel key={k}><ListEditor label={label} testid={`profile-${k.replace(/_/g, "-")}`} items={draft[k] || []} onChange={(v) => set(k, v)} /></Panel>)}
          </div>
          <Panel title="Contact">
            <div className="grid gap-5 md:grid-cols-2">
              {CONTACT.map((k) => <Field key={k} label={k.replace("_", " ")}><input data-testid={`profile-contact-${k}-input`} className="cc-input" value={draft.contact?.[k] || ""} onChange={(e) => setContact(k, e.target.value)} /></Field>)}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
